import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaGraduationCap, FaFire } from 'react-icons/fa';

function ExamCountdown({ examDate, targetSchools = [] }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [urgencyLevel, setUrgencyLevel] = useState('normal'); // normal, warning, critical

  useEffect(() => {
    const calculateTimeLeft = () => {
      const examDateTime = new Date(examDate);
      const now = new Date();
      const difference = examDateTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        setTimeLeft({ days, hours, minutes, seconds });

        // Déterminer le niveau d'urgence
        if (days <= 7) {
          setUrgencyLevel('critical');
        } else if (days <= 30) {
          setUrgencyLevel('warning');
        } else {
          setUrgencyLevel('normal');
        }
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [examDate]);

  const getUrgencyColors = () => {
    switch (urgencyLevel) {
      case 'critical':
        return {
          bg: 'from-red-600 to-red-800',
          border: 'border-red-400',
          text: 'text-red-100',
          accent: 'text-yellow-300'
        };
      case 'warning':
        return {
          bg: 'from-orange-500 to-red-600',
          border: 'border-orange-400',
          text: 'text-orange-100',
          accent: 'text-yellow-200'
        };
      default:
        return {
          bg: 'from-blue-600 to-indigo-700',
          border: 'border-blue-400',
          text: 'text-blue-100',
          accent: 'text-yellow-300'
        };
    }
  };

  const colors = getUrgencyColors();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl border-4 ${colors.border} shadow-2xl`}
      data-testid="exam-countdown"
    >
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg}`}></div>
      
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-full">
              <FaGraduationCap className={`text-2xl ${colors.accent}`} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px' }}>
                COMPTE À REBOURS
              </h3>
              <p className={`text-sm ${colors.text}`}>
                {formatDate(examDate)}
              </p>
            </div>
          </div>
          {urgencyLevel === 'critical' && (
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="flex items-center gap-1 bg-yellow-400 text-red-800 px-3 py-1 rounded-full font-bold text-sm"
            >
              <FaFire /> URGENT
            </motion.div>
          )}
        </div>

        {/* Countdown boxes */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { value: timeLeft.days, label: 'JOURS' },
            { value: timeLeft.hours, label: 'HEURES' },
            { value: timeLeft.minutes, label: 'MIN' },
            { value: timeLeft.seconds, label: 'SEC' }
          ].map((item, idx) => (
            <motion.div
              key={item.label}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center border border-white/30"
            >
              <motion.span
                key={item.value}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="block text-3xl md:text-4xl font-bold text-white"
                style={{ fontFamily: 'Bangers, cursive', textShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}
              >
                {String(item.value).padStart(2, '0')}
              </motion.span>
              <span className="text-xs font-semibold text-white/80" style={{ fontFamily: 'Bebas Neue' }}>
                {item.label}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Target schools */}
        {targetSchools.length > 0 && (
          <div className="border-t border-white/20 pt-4">
            <p className="text-xs text-white/70 mb-2 flex items-center gap-1">
              <FaCalendarAlt /> Écoles visées:
            </p>
            <div className="flex flex-wrap gap-2">
              {targetSchools.map((school, idx) => (
                <span
                  key={idx}
                  className="bg-white/20 text-white text-xs px-3 py-1 rounded-full border border-white/30"
                >
                  {school}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Motivation message */}
        {urgencyLevel === 'critical' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 p-3 bg-yellow-400/20 rounded-lg border border-yellow-400/50"
          >
            <p className="text-yellow-200 text-sm font-medium text-center">
              🎯 Dernière ligne droite ! Concentre-toi sur tes points faibles.
            </p>
          </motion.div>
        )}
        {urgencyLevel === 'warning' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 p-3 bg-orange-400/20 rounded-lg border border-orange-400/50"
          >
            <p className="text-orange-200 text-sm font-medium text-center">
              ⚡ Plus qu'un mois ! Maintiens ton rythme d'entraînement quotidien.
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default ExamCountdown;
